import { useEffect, useMemo, useState } from 'react';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface ProjectContactFormProps {
  projectId: string;
  projectName?: string;
  referenceCode?: string;
  assignedAgentId?: string | null;
  onSuccess?: () => void;
}

interface UnitType {
  id: string;
  name: string;
  bedrooms: number | null;
  area_min: number | null;
  area_max: number | null;
  price_min: number | null;
  price_max: number | null;
  currency: string | null;
}

const formatUnitLabel = (u: UnitType) =>
  `${u.name}${u.bedrooms ? ` · ${u.bedrooms}BR` : ''}${u.area_min ? ` · ${u.area_min}${u.area_max && u.area_max !== u.area_min ? `–${u.area_max}` : ''} m²` : ''}`;

export function ProjectContactForm({
  projectId, projectName, referenceCode, assignedAgentId, onSuccess,
}: ProjectContactFormProps) {
  const { user, profile } = useAuth();
  const refId = (referenceCode || `ID${projectId.slice(0, 8)}`).toUpperCase();
  const displayName = projectName || 'this project';

  const [units, setUnits] = useState<UnitType[]>([]);
  const [unitId, setUnitId] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [msgEdited, setMsgEdited] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const selectedUnit = useMemo(() => units.find(u => u.id === unitId), [units, unitId]);

  useEffect(() => {
    setName(profile?.full_name || profile?.name || '');
    setPhone(profile?.phone || '');
    setEmail(user?.email || '');
  }, [profile, user]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('project_unit_types')
        .select('id, name, bedrooms, area_min, area_max, price_min, price_max, currency')
        .eq('project_id', projectId)
        .order('sort_order', { ascending: true });
      setUnits(data || []);
    })();
  }, [projectId]);

  useEffect(() => {
    if (msgEdited) return;
    setMessage(`I'd like to reserve a unit at ${displayName} (${refId}). Selected unit: ${selectedUnit?.name || '—'}. Please contact me with availability and next steps.`);
  }, [selectedUnit, displayName, refId, msgEdited]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      toast.error('Please fill in your name and phone number');
      return;
    }
    setSubmitting(true);
    try {
      const finalMessage = `${message.trim()}\n\n[Unit: ${selectedUnit?.name || '—'} | Project Ref: ${refId}]`;
      const { error } = await supabase.from('leads').insert({
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim() || null,
        message: finalMessage,
        project_id: projectId,
        source: 'project_page',
        agent_id: assignedAgentId || null,
        assigned_to: assignedAgentId || null,
        sender_id: user?.id ?? null,
      });
      if (error) throw error;
      toast.success('Your message has been sent!');
      setMsgEdited(false);
      onSuccess?.();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to send. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span className="truncate">{displayName}</span>
        <Badge variant="outline" className="font-mono text-[10px] shrink-0">{refId}</Badge>
      </div>

      {units.length > 0 && (
        <div>
          <Label className="text-xs font-medium mb-1 block">Available Unit</Label>
          <Select value={unitId} onValueChange={setUnitId}>
            <SelectTrigger><SelectValue placeholder="Select a unit" /></SelectTrigger>
            <SelectContent>
              {units.map(u => (
                <SelectItem key={u.id} value={u.id}>{formatUnitLabel(u)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <Label className="text-xs font-medium mb-1 block">Name <span className="text-destructive">*</span></Label>
        <Input value={name} onChange={e => setName(e.target.value)} required />
      </div>

      <div>
        <Label className="text-xs font-medium mb-1 block">Phone <span className="text-destructive">*</span></Label>
        <div className="flex gap-2">
          <div className="flex items-center gap-1 border rounded-md px-2 text-sm text-muted-foreground shrink-0">🇪🇹 +251</div>
          <Input value={phone} onChange={e => setPhone(e.target.value)} required />
        </div>
      </div>

      <div>
        <Label className="text-xs font-medium mb-1 block">Email</Label>
        <Input type="email" value={email} onChange={e => setEmail(e.target.value)} />
      </div>

      <div>
        <Label className="text-xs font-medium mb-1 block">Message</Label>
        <Textarea
          rows={4}
          value={message}
          onChange={e => { setMessage(e.target.value); setMsgEdited(true); }}
        />
      </div>

      <Button type="submit" className="w-full h-11 font-bold gap-2" disabled={submitting}>
        <Send className="h-4 w-4" />
        {submitting ? 'Sending...' : 'Send Message'}
      </Button>
      <p className="text-[10px] text-muted-foreground text-center">
        By submitting, you agree to our Terms of Use.
      </p>
    </form>
  );
}
